import type { ReactNode } from 'react'
import { fmtInt } from '../lib/format'

type Props = {
  label: string
  value: number | string | null | undefined
  unit?: string
  tone?: 'paper' | 'amber' | 'cyan' | 'red' | 'green'
  delta?: number
  sub?: ReactNode
  size?: 'sm' | 'md' | 'lg'
}

const toneClass: Record<NonNullable<Props['tone']>, string> = {
  paper: 'text-paper',
  amber: 'text-amber-glow',
  cyan: 'text-radar-cyan',
  red: 'text-radar-red',
  green: 'text-radar-green',
}

const sizeClass: Record<NonNullable<Props['size']>, string> = {
  sm: 'text-2xl',
  md: 'text-4xl',
  lg: 'text-6xl',
}

export function Metric({ label, value, unit, tone = 'paper', delta, sub, size = 'md' }: Props) {
  const displayValue = typeof value === 'number' ? fmtInt(value) : value ?? '—'
  return (
    <div className="flex flex-col gap-1">
      <span className="label">{label}</span>
      <div className="flex items-baseline gap-2">
        <span
          className={`font-mono font-semibold ${sizeClass[size]} ${toneClass[tone]} leading-none`}
          style={{ fontVariantNumeric: 'tabular-nums slashed-zero' }}
        >
          {displayValue}
        </span>
        {unit && <span className="label text-paper-faint">{unit}</span>}
        {delta !== undefined && (
          <span className={`label ${delta > 0 ? 'text-radar-green' : delta < 0 ? 'text-radar-red' : 'text-paper-faint'}`}>
            {delta > 0 ? '▲' : delta < 0 ? '▼' : '—'} {Math.abs(delta)}
          </span>
        )}
      </div>
      {sub && <div className="text-[11px] text-paper-dim font-mono">{sub}</div>}
    </div>
  )
}
